// Gedeelde visuele tokens voor task-rijen en chips — enkele bron voor TaskItem-styling.
import { actionIconButtonSx } from '@/components/ui/actionIconButtonSx';

/** Shared visual tokens for task rows (TaskItem) — single source of truth. */

export const TASK_ROW_MIN_HEIGHT = 48;
export const TASK_ROW_PADDING_Y = 2;
export const TASK_ROW_PADDING_X = 2.5;
export const TASK_ROW_GAP = 1;
export const TASK_ROW_MB = 1.5;

/** WCAG 2.5.8 minimum pointer target (px). */
export const TASK_MIN_HIT_TARGET_PX = 24;
/** MUI Checkbox `size="small"` icon width (px). */
export const PLAN_CHECKBOX_ICON_PX = 20;

export const TASK_ROW_BORDER_LEFT_DEFAULT = 1;
export const TASK_ROW_BORDER_LEFT_ACCENT = 4;

export const taskRowSelectedBg = 'background.subtle';

/**
 * @param {string|undefined} accent — explicit accent color (e.g. overdue)
 * @param {boolean} selected
 */
export function taskRowBorderLeftSx(accent, selected) {
  const hasAccent = accent || selected;
  return {
    borderLeft: hasAccent
      ? TASK_ROW_BORDER_LEFT_ACCENT
      : TASK_ROW_BORDER_LEFT_DEFAULT,
    borderLeftColor: accent || (selected ? 'primary.main' : 'neutral.border'),
  };
}

export const taskChipSx = {
  fontWeight: 600,
  height: 20,
  fontSize: '0.75rem',
  '& .MuiChip-label': { px: 1 },
};

export const taskChipContainerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexShrink: 0,
};

export const taskDeleteButtonSx = {
  opacity: 0,
  transition: 'opacity 0.1s',
  p: 0.25,
  color: 'text.secondary',
  '&:hover': { color: 'error.main' },
};

export const taskPlayButtonSx = {
  width: 18,
  height: 18,
  p: 0,
  ...actionIconButtonSx,
};

export const taskPlayIconSx = { fontSize: 11 };

/** Pause uses warning fill to distinguish from play (secondary purple). */
export const taskPauseButtonSx = {
  width: 18,
  height: 18,
  p: 0,
  bgcolor: 'warning.main',
  color: 'common.white',
  '&:hover': { bgcolor: 'warning.dark' },
};

export const taskPauseIconSx = { fontSize: 11 };

/** Shared calendar title size for day time-grid blocks and week grid items. */
export const planCalendarTaskTitleFontSize = '0.95em';
export const planDayItemFontSize = planCalendarTaskTitleFontSize;
/** Absolute px so one-slot blocks stay readable (avoids compounding with gridTaskItemSx em). */
export const planDayItemShortFontSize = '11px';

export const PLAN_LINE_HEIGHT_SHORT = 1.1;
export const PLAN_LINE_HEIGHT_DEFAULT = 1.2;
export const PLAN_LINE_HEIGHT_TASK = 1.3;

export const GRID_TASK_DRAG_OPACITY = 0.3;
export const COMPLETED_TASK_OPACITY = 0.75;

export const FONT_WEIGHT_SEMIBOLD = 600;
export const FONT_WEIGHT_REGULAR = 400;

export const gridTaskItemSx = {
  p: '4px 6px',
  mb: '4px',
  borderRadius: '3px',
  bgcolor: 'grey.200',
  border: 1,
  borderColor: 'neutral.border',
  fontSize: '0.72em',
  lineHeight: 1.25,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  columnGap: '4px',
  minWidth: 0,
};

export const gridTaskItemDenseSx = {
  ...gridTaskItemSx,
  fontSize: '0.62em',
  p: '1px 4px',
  mb: '2px',
  lineHeight: 1.15,
};

export const gridTaskItemRoomySx = {
  ...gridTaskItemSx,
  fontSize: planCalendarTaskTitleFontSize,
};

/** External calendar events — dashed/italic so they never look like tasks. */
export const gcalEventItemSx = {
  ...gridTaskItemSx,
  bgcolor: 'transparent',
  border: '1px dashed',
  borderColor: 'text.disabled',
  borderRadius: '2px',
  fontStyle: 'italic',
  color: 'text.subtle',
  fontWeight: 400,
};

export const gcalEventItemDenseSx = {
  ...gcalEventItemSx,
  ...gridTaskItemDenseSx,
  bgcolor: 'transparent',
  border: '1px dashed',
  borderColor: 'text.disabled',
  fontStyle: 'italic',
  color: 'text.subtle',
  fontWeight: 400,
};

/** Neutral checkbox on Plan day-blocks (not primary/secondary). */
export const planTaskCheckboxSx = {
  p: `${(TASK_MIN_HIT_TARGET_PX - PLAN_CHECKBOX_ICON_PX) / 2}px`,
  width: TASK_MIN_HIT_TARGET_PX,
  height: TASK_MIN_HIT_TARGET_PX,
  boxSizing: 'border-box',
  mt: '1px',
  color: 'text.disabled',
  '&.Mui-checked': { color: 'text.disabled' },
};

/**
 * 15-minute slots (20px tall): full height × 24px wide — documented exception.
 * @see taken/click-targets-24px-min.md
 */
export const planTaskCheckboxShortSx = {
  ...planTaskCheckboxSx,
  p: 0,
  width: TASK_MIN_HIT_TARGET_PX,
  minWidth: TASK_MIN_HIT_TARGET_PX,
  minHeight: 0,
  alignSelf: 'stretch',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/** Context chips: outlined, not filled — distinct from project identity chips. */
export const contextChipSx = {
  ...taskChipSx,
  bgcolor: 'transparent',
  color: 'text.secondary',
  border: 1,
  borderColor: 'divider',
};
